/* ────────────────────────────────────────────────────────────────────────────
   OptimizedModule.js
   tiny layout tweaks + restored rocket animation
   headline/description always left-aligned
──────────────────────────────────────────────────────────────────────────── */
import React, { useState, useEffect, Suspense } from "react";
import tw from "twin.macro";
import styled, { css } from "styled-components/macro"; // eslint-disable-line
import { Link } from "react-router-dom";
import Navigation from "_content/Navigation.js";
import ChartAndFeatures from "components/custom/ChartAndFeatures.js";
import ExchangeLogos from "components/custom/home/ExchangeLogos.js";
import HeaderBase, {
  LogoLink as LogoLinkBase,
} from "components/headers/light.js";
import {
  Container as ContainerBase,
  ContentWithVerticalPadding,
  Content2Xl,
} from "components/misc/Layouts.js";
import { SectionHeading } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import logoImageSrc from "images/logo-light.svg";

const isClient = typeof window !== "undefined";
const isMobile =
  isClient && window.matchMedia("(max-width: 768px)").matches;

/* ───────────────────────────────── BACKGROUND ───────────────────────────── */
const PrimaryBackgroundContainer = styled.div`
  ${tw`-mx-8 px-8 pt-0 mt-0 text-gray-100`}
  background-color:#0f172a;
  background-image:
    radial-gradient(circle at 60% 70%,rgba(15,35,55,.9)0%,transparent 70%),
    radial-gradient(circle at 40% 20%,rgba(20,40,60,.55)0%,transparent 70%);
`;

const Header = tw(HeaderBase)`max-w-none -mt-8 py-8 -mx-8 px-8`;
const LogoLink = tw(LogoLinkBase)`text-gray-100 -mt-4`;

/* ──────────────────────────────── LAYOUT ───────────────────────────────── */
const Container = styled(ContainerBase)`
  margin-top:-3rem; /* negative margin kept, reduced */
  ${tw`lg:-mt-8`}
`;
const Row = tw.div`flex flex-col-reverse lg:flex-row items-center gap-y-12`;
const Column = tw.div`lg:w-1/2`;
const TextColumn = tw(Column)`text-left`; /* <- always left-aligned */
const IllustrationColumn = tw(Column)`
  hidden md:block md:ml-12 lg:ml-16
`;

/* ─────────────────────────── TYPOGRAPHY ────────────────────────────────── */
const Heading = tw(
  SectionHeading
)`max-w-3xl lg:max-w-4xl leading-tight md:text-5xl text-3xl text-left`;

const Description = tw(
  SectionDescription
)`mt-4 max-w-2xl lg:text-base text-sm text-gray-300 text-left`;

/* ─────────────────────────── ACTION BUTTONS ───────────────────────────── */
const Actions = styled.div`
  ${tw`flex flex-row items-start mt-6 mb-10 gap-4`}
  .action{
    ${tw`
      inline-block px-6 py-3 font-semibold rounded
      tracking-wide text-center transition duration-300
      focus:outline-none focus:shadow-outline
    `}
  }
  .primaryAction{${tw`bg-primary-500 hover:bg-primary-600 text-white`}}
  .secondaryAction{${tw`bg-gray-700 hover:bg-gray-600 text-gray-100`}}
`;

/* ─────────────────────── ROCKET ANIMATION STYLES ──────────────────────── */
const RocketAnim = css`
  @keyframes rocket-float{
    0%,100%{transform:translateY(0) rotate(-3deg) scale(1)}
    25%{transform:translateY(-12px) rotate(2deg) scale(1.05)}
    50%{transform:translateY(-8px) rotate(-2deg) scale(1.02)}
    75%{transform:translateY(-15px) rotate(3deg) scale(1.08)}
  }
  @keyframes sparkle{
    0%,100%{opacity:0;transform:scale(0)}
    50%{opacity:1;transform:scale(1)}
  }
  .rocket-container{
    position:relative;
    animation:rocket-float 4s infinite cubic-bezier(.4,0,.2,1);
    will-change:transform;
  }
  .rocket-container:hover{
    animation-play-state:paused;
    transform:scale(1.15) translateY(-5px);
  }
  .sparkle{
    position:absolute;width:4px;height:4px;border-radius:50%;
    background:#fff;box-shadow:0 0 10px rgba(255,255,255,.8);
    animation:sparkle 2s infinite;
  }
  .s1{top:-5px;left:10px;animation-delay:.1s}
  .s2{top:10px;right:-5px;animation-delay:.5s}
  .s3{bottom:0;left:0;animation-delay:.8s}
  .s4{top:0;right:10px;animation:sparkle 2.5s infinite 1.2s}
`;

/* ───────────────────────────── PROMO BANNER ───────────────────────────── */
const PromoWrapper = styled.div`
  ${tw`max-w-2xl mx-auto bg-gradient-to-r from-gray-900 via-teal-900 to-gray-800
       border border-teal-500 rounded-lg shadow-lg mt-6
       transition-all duration-300 hover:border-teal-400`}
  ${RocketAnim}
`;
const PromoInner = tw.div`
  flex flex-col md:flex-row items-center justify-between gap-4 px-6 py-4
`;
const PromoText = tw.div`flex items-center gap-4`;
const PromoTitle = tw.h3`text-center md:text-left font-bold text-lg tracking-wide text-teal-400`;
const PromoLabel = tw.div`
  bg-teal-600 border border-teal-500 rounded-full
  text-white font-bold text-sm px-4 py-2
`;

const Promo = ({ locale = "en" }) => (
  <a
    href={
      locale === "es"
        ? "https://www.gunbot.com/es/planes/"
        : "https://www.gunbot.com/plans/"
    }
    rel="noopener noreferrer"
  >
    <PromoWrapper>
      <PromoInner>
        <div tw="hidden md:block">
          <div className="rocket-container select-none cursor-pointer">
            <div className="sparkle s1" />
            <div className="sparkle s2" />
            <div className="sparkle s3" />
            <div className="sparkle s4" />
            <p tw="text-4xl mt-4">🚀</p>
          </div>
        </div>

        <PromoText>
          <div>
            <PromoTitle>
              {locale === "es" ? "PROMOCIONES CRYPTO" : "ATH SPECIALS"}
            </PromoTitle>
            <p tw="text-center md:text-left text-white font-semibold">
              {locale === "es"
                ? "¡Nuevos precios bajos de por vida!"
                : "New low lifetime prices!"}
            </p>
          </div>
        </PromoText>

        {!isMobile &&
          <PromoLabel>
            {locale === "es" ? "DESCUENTO $250" : "$250 OFF"}
          </PromoLabel>
        }

      </PromoInner>
    </PromoWrapper>
  </a>
);

/* ───────────────────────────── RECENT POSTS ───────────────────────────── */
const Blogpost = tw.a`
  border-l-2 border-blue-600 pl-2 font-medium text-sm text-left text-gray-100
`;
const RecentPosts = ({ translations }) => {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    if (isMobile) return;
    fetch("https://www.gunbot.com/blog/rss.xml")
      .then((res) => res.text())
      .then((xml) => {
        const doc = new DOMParser().parseFromString(xml, "text/xml");
        const items = Array.from(doc.querySelectorAll("item")).slice(0, 3);
        setPosts(
          items.map((i) => ({
            title: i.querySelector("title")?.textContent ?? "",
            link: i.querySelector("link")?.textContent ?? "#",
          }))
        );
      })
      .catch((e) => console.error("RSS fetch error", e));
  }, []);
  const { hero } = translations;
  return (
    <div tw="hidden lg:block mt-10">
      <strong tw="text-white">{hero.recentNewsAndArticlesText}</strong>
      {posts.map((p) => (
        <div key={p.link}>
          <Blogpost href={`${p.link}/`} target="_blank" rel="noopener noreferrer">
            {p.title}
          </Blogpost>
        </div>
      ))}
    </div>
  );
};

/* ─────────────────────────────── CHART ────────────────────────────────── */
const ChartContainer = tw(ChartAndFeatures)``;

/* ───────────────────────────── MAIN EXPORT ────────────────────────────── */
const OptimizedModule = ({ translations }) => {
  const { hero } = translations;
  const locale =
    isClient && window.location.pathname.startsWith("/es") ? "es" : "en";

  const logoLink = (
    <LogoLink to={locale === "es" ? "/es/" : "/"}>
      <img src={logoImageSrc} alt="Gunbot logo" width={40} height={40} />
      Gunbot
    </LogoLink>
  );

  return (
    <PrimaryBackgroundContainer>
      <Content2Xl>
        <Header logoLink={logoLink} links={<Navigation />} />

        <Container>
          <ContentWithVerticalPadding>
            <Row>
              <TextColumn>
                <Heading>{hero.heading}</Heading>
                <Description>{hero.description}</Description>

                <Actions>
                  <a
                    href={hero.plansLink}
                    className="action primaryAction"
                    title={hero.comparePlansButton}
                    rel="noopener noreferrer"
                  >
                    {hero.comparePlansButton}
                  </a>
                  <a
                    href={hero.performanceLink}
                    className="action secondaryAction"
                    title={hero.checkPerformanceButton}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {hero.checkPerformanceButton}
                  </a>
                </Actions>

                <Promo locale={locale} />

                {!isMobile && (
                  <Suspense fallback={<div>Loading…</div>}>
                    <RecentPosts translations={translations} />
                  </Suspense>
                )}
              </TextColumn>

              {!isMobile && (
                <IllustrationColumn css={css`margin-top:-4.5rem;`}>
                  <ChartContainer
                    translations={translations}
                    css={css`
                      width:640px;max-width:100%;
                    `}
                  />
                </IllustrationColumn>
              )}
            </Row>

            <ExchangeLogos />
          </ContentWithVerticalPadding>
        </Container>
      </Content2Xl>
    </PrimaryBackgroundContainer>
  );
};

export default React.memo(OptimizedModule);
